import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Content from '../../components/Content';
import Loader from '../../components/Loader';
import Error from '../../components/Error';
import { ErrorCodes } from '../../utils/errorCodes';
import { formatFrontendSupportError } from '../../utils/frontendErrorFormat';
import styles from './NetZones.module.css';

const FRONTEND_IMPORT_NET_ZONES_ERROR_CODES = {
    INITIAL_LOAD_FAILED: ErrorCodes.API_INTERNAL_ERROR,
    ADD_ENTRY_FAILED: ErrorCodes.API_INTERNAL_ERROR,
    VALIDATION_FAILED: ErrorCodes.API_INVALID_PARAMETER,
};

const buildImportNetZonesSupportError = (errorCode, pageInfo, sourceMessage) =>
    formatFrontendSupportError({
        errorCode,
        pageInfo,
        sourceMessage,
    });

const ImportNetZones = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [success, setSuccess] = useState(false);
    const [availableCompanies, setAvailableCompanies] = useState([]);
    const [supportErrorMessage, setSupportErrorMessage] = useState('');
    const [copiedSupportError, setCopiedSupportError] = useState(false);

    // Single entry state - updated to match database schema
    const [singleEntry, setSingleEntry] = useState({
        company: '',
        zone: '',
        startIP: '',
        endIP: '',
        error: ''
    });

    const setImportNetZonesSupportError = useCallback((errorCode, pageInfo, sourceMessage) => {
        setSupportErrorMessage(buildImportNetZonesSupportError(errorCode, pageInfo, sourceMessage));
        setCopiedSupportError(false);
    }, []);

    const clearImportNetZonesSupportError = useCallback((force = false) => {
        if (!force) return;
        setSupportErrorMessage('');
        setCopiedSupportError(false);
    }, []);

    const handleCopySupportError = useCallback(async () => {
        if (!supportErrorMessage) return;
        try {
            await navigator.clipboard.writeText(supportErrorMessage);
            setCopiedSupportError(true);
            setTimeout(() => setCopiedSupportError(false), 2000);
        } catch (copyError) {
            setCopiedSupportError(false);
        }
    }, [supportErrorMessage]);

    // Fetch user session and available companies
    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await axios.get('/api/upload-net-zones');
                if (response.data && response.data.status) {
                    const availableTargets = response.data.available_upload_targets || [];
                    
                    setAvailableCompanies(availableTargets);
                    
                    if (availableTargets.length > 0) {
                        setSingleEntry(prev => ({ ...prev, company: availableTargets[0] }));
                    }
                    clearImportNetZonesSupportError(true);
                } else {
                    setError(true);
                    setImportNetZonesSupportError(
                        FRONTEND_IMPORT_NET_ZONES_ERROR_CODES.INITIAL_LOAD_FAILED,
                        'Import NetZones Page / Initial Data Load Failed',
                        response?.data?.msg || 'Initial upload-net-zones request did not return success'
                    );
                }
            } catch (err) {
                console.error('Error fetching initial data:', err);
                setError(true);
                setImportNetZonesSupportError(
                    FRONTEND_IMPORT_NET_ZONES_ERROR_CODES.INITIAL_LOAD_FAILED,
                    'Import NetZones Page / Initial Data Request Error',
                    err?.response?.data?.msg || err?.message || String(err)
                );
            }
        };
        
        fetchData();
    }, [clearImportNetZonesSupportError, setImportNetZonesSupportError]);

    // Basic IP address validation
    const isValidIP = (ip) => {
        const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
        return ipRegex.test(ip);
    };

    const handleAddSingleEntry = async (e) => {
        e.preventDefault();
        
        // Validate required fields
        if (!singleEntry.zone.trim() || !singleEntry.startIP.trim() || !singleEntry.endIP.trim()) {
            setSingleEntry({ 
                ...singleEntry, 
                error: 'Zone name, Start IP, and End IP are required' 
            });
            setImportNetZonesSupportError(
                FRONTEND_IMPORT_NET_ZONES_ERROR_CODES.VALIDATION_FAILED,
                'Import NetZones Page / Single Entry Validation Failed',
                'Zone, Start IP, and End IP are required'
            );
            return;
        }

        // Validate IP addresses
        if (!isValidIP(singleEntry.startIP.trim())) {
            setSingleEntry({ 
                ...singleEntry, 
                error: 'Start IP must be a valid IP address (e.g., 192.168.1.1)' 
            });
            setImportNetZonesSupportError(
                FRONTEND_IMPORT_NET_ZONES_ERROR_CODES.VALIDATION_FAILED,
                'Import NetZones Page / Start IP Validation Failed',
                'Start IP is not a valid IPv4 address'
            );
            return;
        }

        if (!isValidIP(singleEntry.endIP.trim())) {
            setSingleEntry({ 
                ...singleEntry, 
                error: 'End IP must be a valid IP address (e.g., 192.168.1.254)' 
            });
            setImportNetZonesSupportError(
                FRONTEND_IMPORT_NET_ZONES_ERROR_CODES.VALIDATION_FAILED,
                'Import NetZones Page / End IP Validation Failed',
                'End IP is not a valid IPv4 address'
            );
            return;
        }

        setLoading(true);
        
        try {
            const companyToUse = singleEntry.company;
            // Create CSV data matching the expected format: StartIP, EndIP, Zone
            const csvData = `StartIP,EndIP,Zone\n${singleEntry.startIP.trim()},${singleEntry.endIP.trim()},${singleEntry.zone.trim()}`;
            
            const formData = new FormData();
            const csvBlob = new Blob([csvData], { type: 'text/csv' });
            formData.append('netZonesFile', csvBlob, 'single_entry.csv');
            
            if (companyToUse) {
                formData.append('targetCompany', companyToUse);
            }
            
            const response = await axios.post('/api/upload-net-zones', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            
            if (response.data.status) {
                setSuccess(true);
                clearImportNetZonesSupportError(true);
                
                // Clear the form after successful submission
                setTimeout(() => {
                    setSingleEntry({
                        company: availableCompanies[0] || '',
                        zone: '',
                        startIP: '',
                        endIP: '',
                        error: ''
                    });
                    setSuccess(false);
                }, 3000);
            } else {
                const sourceMessage = response?.data?.msg || 'Failed to add NetZone entry. Please try again.';
                setSingleEntry({ 
                    ...singleEntry, 
                    error: sourceMessage 
                });
                setImportNetZonesSupportError(
                    FRONTEND_IMPORT_NET_ZONES_ERROR_CODES.ADD_ENTRY_FAILED,
                    'Import NetZones Page / Add NetZone Failed',
                    sourceMessage
                );
            }
        } catch (error) {
            console.error('Error adding NetZone entry:', error);
            const sourceMessage = error.response?.data?.msg || error?.message || 'Failed to add NetZone entry. Please try again.';
            setSingleEntry({ 
                ...singleEntry, 
                error: sourceMessage 
            });
            setImportNetZonesSupportError(
                FRONTEND_IMPORT_NET_ZONES_ERROR_CODES.ADD_ENTRY_FAILED,
                'Import NetZones Page / Add NetZone Request Error',
                sourceMessage
            );
        } finally {
            setLoading(false);
        }
    };

    if (error) {
        return (
            <Content headerTitle="Add Network Zone" currentMenu="netzones">
                <Error />
                {supportErrorMessage && (
                    <div className={styles.supportErrorCard}>
                        <strong>Support Error:</strong>
                        <code className={styles.supportErrorCode}>{supportErrorMessage}</code>
                        <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
                            <button
                                type='button'
                                className={styles.copySupportErrorButton}
                                onClick={handleCopySupportError}
                            >
                                {copiedSupportError ? 'Copied' : 'Copy Error Details'}
                            </button>
                            <button
                                type='button'
                                className={styles.copySupportErrorButton}
                                onClick={() => clearImportNetZonesSupportError(true)}
                            >
                                Clear
                            </button>
                        </div>
                    </div>
                )}
            </Content>
        );
    }

    return (
        <Content headerTitle="Add Network Zone" currentMenu="netzones">
            {loading && <Loader />}
            {supportErrorMessage && (
                <div className={styles.supportErrorBanner}>
                    <div className={styles.supportErrorBannerText}>
                        <strong>Support Error:</strong>
                        <code className={styles.supportErrorCode}>{supportErrorMessage}</code>
                    </div>
                    <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
                        <button
                            type='button'
                            className={styles.copySupportErrorButton}
                            onClick={handleCopySupportError}
                        >
                            {copiedSupportError ? 'Copied' : 'Copy Error Details'}
                        </button>
                        <button
                            type='button'
                            className={styles.copySupportErrorButton}
                            onClick={() => clearImportNetZonesSupportError(true)}
                        >
                            Clear
                        </button>
                    </div>
                </div>
            )}
            
            <div className={styles.container}>
                <div className={styles.importOptionsContainer}>
                    {/* Single NetZone Entry Section */}
                    <div className={styles.importSection}>
                        <div className={styles.importHeader}>
                            <h3 className={styles.importTitle}>Add NetZone</h3>
                            <p className={styles.importSubtitle}>Add a network zone entry manually</p>
                        </div>
                        
                        <div className={styles.importCard}>
                            <form onSubmit={handleAddSingleEntry} className={styles.singleRuleForm}>
                                {availableCompanies.length > 0 && (
                                    <div className={styles.inputGroup}>
                                        <label className={styles.formLabel}>Company</label>
                                        <select
                                            value={singleEntry.company}
                                            onChange={(e) => setSingleEntry({ ...singleEntry, company: e.target.value })}
                                            className={styles.companySelect}
                                        >
                                            {availableCompanies.map(company => (
                                                <option key={company} value={company}>{company}</option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                                
                                <div className={styles.inputGroup}>
                                    <label className={styles.formLabel}>Zone Name *</label>
                                    <input
                                        type="text"
                                        value={singleEntry.zone}
                                        onChange={(e) => setSingleEntry({ ...singleEntry, zone: e.target.value })}
                                        placeholder="e.g., DMZ, Internal LAN, Guest Network"
                                        className={styles.formInput}
                                        required
                                    />
                                </div>
                                
                                <div className={styles.formRow}>
                                    <div className={styles.inputGroup}>
                                        <label className={styles.formLabel}>Start IP Address *</label>
                                        <input
                                            type="text"
                                            value={singleEntry.startIP}
                                            onChange={(e) => setSingleEntry({ ...singleEntry, startIP: e.target.value })}
                                            placeholder="e.g., 192.168.1.1"
                                            className={styles.formInput}
                                            required
                                        />
                                    </div>
                                    
                                    <div className={styles.inputGroup}>
                                        <label className={styles.formLabel}>End IP Address *</label>
                                        <input
                                            type="text"
                                            value={singleEntry.endIP}
                                            onChange={(e) => setSingleEntry({ ...singleEntry, endIP: e.target.value })}
                                            placeholder="e.g., 192.168.1.254"
                                            className={styles.formInput}
                                            required
                                        />
                                    </div>
                                </div>
                                
                                {singleEntry.error && (
                                    <div className={styles.errorMessage}>{singleEntry.error}</div>
                                )}
                                
                                {success && (
                                    <div className={styles.successMessage}>NetZone added successfully!</div>
                                )}
                                
                                <button type="submit" className={styles.submitButton} disabled={loading}>
                                    {loading ? 'Adding...' : 'Add NetZone'}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </Content>
    );
};

export default ImportNetZones;
